import { useMemo, useState } from 'react';
import {
  DateFormats,
  IPlanPriceV3,
  IPlanV3,
  PHONE_NUMBER_COUNTRIES,
  StripePricesLookupKeys,
  SubscriptionCancelReason,
  SubscriptionStatus,
  allowedSubscriptionStatus,
  currencyFormatter,
  findPlanByLookupKey,
  findPriceByLookupKey,
} from 'shared';
import {
  useLocation,
  useNavigate,
  useParams,
} from 'react-router';
import { When } from 'react-if';
import { format } from 'date-fns';

import { Badge } from '../../../common/Atoms/Badge';
import { Button } from '../../../common/Atoms/Button';
import { Card } from '../../../common/Atoms/Card';
import { CardContent } from '../../../common/Atoms/CardContent';
import { Select } from '../../../common/Atoms/Select';
import { Modal } from '../../../common/Atoms/Modal';
import { Notification } from '../../../common/Atoms/Notification';
import { env } from '../../../common/lib/env';
import { events, logAnalyticEvent } from '../../lib/analytics';
import { useUser } from '../../hooks/useUser';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { useOrganisations } from '../../hooks/useOrganisations';
import {
  useActionSubscriptionMutation,
  useAddOnOrgMutation,
  useCancelSubscriptionMutation,
  useDeleteSubscriptionMutation,
  useLazyFetchSmsUsageInvoicesQuery,
  useUpdateSubscriptionMutation,
} from '../../services/api/subscriptionApi/subscription';
import { useLoading } from '../../hooks/useLoading';
import { usePlans } from '../../hooks/usePlans';
import { Heading } from '../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';

import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { NewPlans } from './NewPlans';
import { SmsLimit } from './SMSLimit';
import { SmsBillingModal } from './SmsBillingModal';
import { SmsInvoicesModal } from './SmsInvoicesModal';
import { UsageMeterRow } from './UsageMeterRow';
import { Wallet } from './Wallet';

export function Subscription() {
  const id = Number(useParams().subscriptionId);
  const { data: subscriptions, activeSubscriptionsCurrency } = useSubscriptions();
  const { data: plans } = usePlans();
  const actionSubscriptionMutation = useActionSubscriptionMutation();
  const navigate = useNavigate();
  const [deleteSubscription, {
    isLoading: deleteLoading,
  }] = useDeleteSubscriptionMutation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [actionSubscription, actionLoadings] = useLoading(actionSubscriptionMutation, `organisationId`) as any;
  const [purchaseOrgAddOn, { isLoading: purchaseOrgAddOnLoading }] = useAddOnOrgMutation();
  const location = useLocation();
  const { data: user } = useUser();
  const { data: organisations } = useOrganisations();
  const [ error, setError ] = useState(``);
  const [ selectedOrgToLink, setSelectedOrgToLink ] = useState(null);
  const [showSmsBillingModal, setShowSmsBillingModal] = useState(false);
  const [showSmsInvoicesModal, setShowSmsInvoicesModal] = useState(false);
  const [showChangePlanSection, setShowChangePlanSection] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelSubscription, { isLoading: cancelLoading }] = useCancelSubscriptionMutation();
  const [updateSubscription, { isLoading: updateSubscriptionLoading }] = useUpdateSubscriptionMutation();
  const [fetchSmsUsageInvoices, { data: smsUsageInvoices = [], isFetching: isSmsInvoicesLoading }] = useLazyFetchSmsUsageInvoicesQuery();

  const subscription = useMemo(() => {
    return subscriptions.find(s => s.id === id);
  }, [ subscriptions]);

  const isLegacy = subscription?.pricingVersion === 2;

  const success = useMemo(() => {

    const params = new URLSearchParams(location.search);
    const success = params.get(`success`);

    if (success) {
      logAnalyticEvent(events.checkoutSuccess, {
        userId: user.id,
      });
    }

    return success;

  }, [location]);

  const planDetails: {
    maxAccountingConnections: number;
    name: string;
    periodStart: string;
    periodEnd: string;
    priceConfig: IPlanPriceV3;
    planConfig: IPlanV3;
  } = useMemo(() => {
    if (!subscription || !subscription.stripeSubscriptionId) return null;

    const {
      periodEnd,
      periodStart,
    } = subscription;
    const priceConfig = findPriceByLookupKey(subscription.mainLookupKey as StripePricesLookupKeys);
    const planConfig = findPlanByLookupKey(subscription.mainLookupKey as StripePricesLookupKeys);

    return {
      maxAccountingConnections: subscription.usage.organisations.max,
      name: priceConfig.name,
      periodStart: periodStart ? format(new Date(Number(periodStart) * 1000), DateFormats.UI_DISPLAY_LONG) : `N/A`,
      periodEnd: periodEnd ? format(new Date(Number(periodEnd) * 1000), DateFormats.UI_DISPLAY_LONG) : `N/A`,
      priceConfig,
      planConfig,
    };
  }, [ subscription ]);

  const statusTextClass = useMemo(() => {
    if (subscription?.status === SubscriptionStatus.ACTIVE) return `text-secondary`;
    if (subscription?.status === SubscriptionStatus.TRIALING) return `text-orange-300`;

    return `text-red-300`;
  }, [ subscription?.status ]);

  const orphanedOrgsOptions = useMemo(() => {
    const currentOrgs = subscription?.organisations?.map(o => o.id);

    return organisations
      .filter(o => !currentOrgs?.includes(o.id))
      .map(o => {

        if (o.subscriptionMetadata?.status === SubscriptionStatus.ACTIVE || o.subscriptionMetadata?.status === SubscriptionStatus.TRIALING) return {
          label: `${o.legalName} (already linked by ${o.billingManagedBy})`,
          value: o.id,
          disabled: true,
        };

        return {
          label: o.connected ? o.legalName : `${o.legalName} (disconnected)`,
          value: o.id,
        };
      });
  }, [ organisations, subscription ]);

  function onManage() {
    (window as Window).location = `${env.apiUrl}/api/v1/payments/create-portal-session?subscriptionId=${id}`;
  }

  function unlinkOrganisation(id) {
    actionSubscription({
      id: subscription.id,
      action: `unlink`,
      organisationId: id,
    });
  }

  function linkOrganisation() {
    actionSubscription({
      id: subscription.id,
      action: `link`,
      organisationId: selectedOrgToLink,
    });
  }

  async function deleteSub() {
    await deleteSubscription({
      id: subscription.id,
    });

    navigate(`/billing/subscriptions`);
  }

  async function onConfirmCancel(reason: SubscriptionCancelReason, comment: string) {
    const result = await cancelSubscription({
      id: subscription.id,
      reason,
      comment: comment || undefined,
    });

    if (`error` in result) {
      setError(`We couldn't cancel your subscription. Please try again or contact support.`);
    }

    setShowCancelModal(false);
  }

  function onPurchaseAddOn() {
    purchaseOrgAddOn({
      subscriptionId: subscription.id,
    });
  }

  async function onToggleSmsBilling() {
    if (!subscription) return;
    const isSmsBillingEnabled = subscription.smsBillingEnabled ?? false;

    await updateSubscription({
      id: subscription.id,
      update: {
        smsBillingEnabled: !isSmsBillingEnabled,
      },
    });

    setShowSmsBillingModal(false);
  }

  function onViewSmsInvoices() {
    setShowSmsInvoicesModal(true);

    fetchSmsUsageInvoices({
      subscriptionId: subscription.id,
    });
  }

  if (!subscription) return null;

  const smsBillingEnabled = subscription.smsBillingEnabled ?? false;
  const showSmsBillingWarning = (
    subscription.status === SubscriptionStatus.ACTIVE &&
    !smsBillingEnabled
  );
  const smsBillingBadgeColor = smsBillingEnabled ? `green` : `red`;
  const smsBillingBadgeLabel = smsBillingEnabled ? `Enabled` : `Disabled`;
  const addOnPriceLabel = (() => {
    if (subscription.currentAddOnPrice && subscription.currentAddOnPrice !== `-`) {
      return `${subscription.currentAddOnPrice} / Month`;
    }

    const selectedCurrency = (
      subscription.currency ||
      activeSubscriptionsCurrency ||
      `USD`
    ).toUpperCase();
    const formatCurrency = currencyFormatter(selectedCurrency);
    const useAnnualAddOn = subscription.mainLookupKey?.includes(`annual`);
    const addOnLookupPriority = useAnnualAddOn ?
      [
        StripePricesLookupKeys.OrgAddOn29Annual,
        StripePricesLookupKeys.OrgAddOnMultiCurrency2026,
        StripePricesLookupKeys.OrgAddOn29,
        StripePricesLookupKeys.OrgAddOn,
      ] :
      [
        StripePricesLookupKeys.OrgAddOnMultiCurrency2026,
        StripePricesLookupKeys.OrgAddOn29,
        StripePricesLookupKeys.OrgAddOn,
      ];

    for (const lookupKey of addOnLookupPriority) {
      const stripePrice = plans.find(p => p.lookup_key === lookupKey);
      const selectedOption = stripePrice?.currency_options?.[selectedCurrency.toLowerCase()];
      const unitAmount = selectedOption?.unit_amount ?? stripePrice?.unit_amount;

      if (unitAmount != null) {
        return `${formatCurrency(unitAmount / 100)} / Month`;
      }
    }

    return `Current monthly rate`;
  })();

  const showPlans = [
    SubscriptionStatus.INACTIVE,
    SubscriptionStatus.CANCELED,
    SubscriptionStatus.NONE,
  ].includes(subscription.status) || !subscription.status;
  const isActiveSubscription = subscription.status === SubscriptionStatus.ACTIVE;

  const maxOrganisations = planDetails?.maxAccountingConnections || 1;
  const orgLimitLabel = (() => {
    if (maxOrganisations <= 1) return `1 organisation included.`;
    if (isLegacy) return `Up to ${maxOrganisations} organisations included.`;

    return `Up to ${maxOrganisations} organisations (+ ${addOnPriceLabel}${maxOrganisations > 2 ? ` x ${maxOrganisations - 1}` : ``}). To reduce this limit, contact us.`;
  })();

  const formatSubscriptionCurrency = currencyFormatter(subscription.currency || activeSubscriptionsCurrency || `USD`);
  const phoneNumbers = subscription.phoneNumbers || [];
  const phoneNumbersPer = subscription.phoneNumbersInterval === `year` ? `Year` : `Month`;
  const phoneNumbersExtra = phoneNumbers.reduce((sum, number) => sum + (number.amountCents || 0), 0);
  const phoneNumberPrice = (cents: number | null) => (cents === null ? `-` : `${formatSubscriptionCurrency(cents / 100)} / ${phoneNumbersPer}`);

  const planDisplayName = isLegacy && planDetails ?
    `${planDetails.name} (Previously ${planDetails.priceConfig?.previouslyNamed})` :
    planDetails?.name;

  function renderLinkOrgButton() {
    if (planDetails?.planConfig?.name !== `Pro`) {
      return (
        <Button
          disabled={ subscription?.organisations?.length >= subscription?.usage?.organisations?.max || !selectedOrgToLink }
          onClick={ linkOrganisation }
        >
          {
            subscription?.organisations?.length >= subscription?.usage?.organisations.max ? `Organisation Limit Reached` : `Link To Subscription`
          }
        </Button>
      );
    }

    if (subscription?.organisations?.length >= subscription?.usage?.organisations?.max) {
      return (
        <Button
          disabled={ purchaseOrgAddOnLoading }
          onClick={ onPurchaseAddOn }
        >
          {
            `Purchase Company Share Add On (${addOnPriceLabel})`
          }
        </Button>
      );
    }

    return (
      <Button
        disabled={ subscription?.organisations?.length >= subscription?.usage?.organisations?.max || !selectedOrgToLink }
        onClick={ linkOrganisation }
      >
        {
          `Link To Subscription`
        }
      </Button>
    );
  }

  return (
    <div>
      <Card id={ `subscription-item-${subscription.id}` }>
        <When condition={ success }>
          <Notification type={ `success` }>
            { `Thank you for choosing a plan - your subscription is now active. ` }<br></br>{ `You can manage your subscription below or ` }<a className={ `underline` }
              href={ `/` } >{ `go to your account dashboard` }</a>{ `.` }
          </Notification>
        </When>
        <When condition={ error }>
          <Notification type={ `error` }>
            { error }
          </Notification>
        </When>
        <CardContent>
          <Heading>
            { `Manage Subscription` }
          </Heading>

          <When condition={ showPlans }>
            <div className={ `mt-6 flex flex-col space-y-6 w-full` }>
              <NewPlans />
              <When condition={ !subscription?.status }>
                <div className={ `flex justify-end` }>
                  <Button
                    color={ `red` }
                    onClick={ deleteSub }
                    loading={ deleteLoading }
                  >
                    { `Delete Subscription` }
                  </Button>
                </div>
              </When>
              <div className={ `py-4` }></div>
            </div>
          </When>

          <When condition={ !showPlans }>
            <div className={ `mt-7 grid grid-cols-1 items-start gap-8 lg:grid-cols-[1fr_320px]` }>
              { /* Main column */ }
              <div className={ `flex flex-col gap-7` }>
                <When condition={ Boolean(subscription.status) }>
                  <div>
                    <Paragraph variant={ `primary` }>
                      { `Usage this period` }
                    </Paragraph>
                    <When condition={ Boolean(planDetails) }>
                      <Paragraph variant={ `help` }>
                        { `${planDetails?.periodStart} – ${planDetails?.periodEnd}` }
                      </Paragraph>
                    </When>
                    <div className={ `mt-3 overflow-hidden rounded-lg border border-gray-200` }>
                      <UsageMeterRow
                        label={ `Invoices` }
                        current={ subscription.usage.invoices.current }
                        max={ subscription.usage.invoices.max }
                      />
                      <UsageMeterRow
                        label={ `Emails` }
                        current={ subscription.usage.emails.current }
                        max={ subscription.usage.emails.max }
                      />
                      <UsageMeterRow
                        label={ `SMS` }
                        current={ subscription.usage.currentSMSUsage ?? 0 }
                        max={ subscription.smsMonthlyLimit ?? 0 }
                        isLast
                      />
                    </div>
                  </div>
                </When>

                <div>
                  <Paragraph variant={ `primary` }>
                    { `Linked Organisations` }
                  </Paragraph>
                  <Paragraph variant={ `help` }>
                    { `Organisations billed under this subscription. ${orgLimitLabel}` }
                  </Paragraph>
                  <div className={ `mt-3 rounded-lg border border-gray-200` }>
                    {
                      subscription.organisations?.map(org => (
                        <div
                          key={ org.id }
                          className={ `flex items-center justify-between border-b border-gray-100 px-5 py-4` }
                        >
                          <div className={ `flex items-center gap-3` }>
                            <div className={ `flex h-9 w-9 items-center justify-center rounded-lg bg-paidniceGreen-100 text-xs font-bold text-paidniceGreen-600` }>
                              { org.legalName?.split(` `).map(w => w[0]).join(``).slice(0, 2).toUpperCase() }
                            </div>
                            <div className={ `text-sm font-semibold text-paidniceGreen-800` }>
                              { org.legalName }
                            </div>
                          </div>
                          <Button
                            color={ `gray` }
                            data-cy={ `unlink-organisation-${org.id}` }
                            loading={ actionLoadings[org.id] }
                            onClick={ () => unlinkOrganisation(org.id) }
                          >
                            { `Unlink` }
                          </Button>
                        </div>
                      )) || null
                    }
                    <When condition={ allowedSubscriptionStatus.includes(subscription.status) }>
                      <div className={ `flex items-end gap-3 rounded-b-lg bg-gray-50 px-5 py-4` }>
                        <div className={ `flex-1` }>
                          <Select
                            label={ `Link Organisation` }
                            nullable
                            options={ orphanedOrgsOptions }
                            selected={ selectedOrgToLink }
                            onChange={ e => setSelectedOrgToLink(e.value) }
                            name={ `organisation select` }
                          />
                        </div>
                        { renderLinkOrgButton() }
                      </div>
                    </When>
                  </div>
                </div>

                <When condition={ phoneNumbers.length > 0 }>
                  <div>
                    <Paragraph variant={ `primary` }>
                      { `Phone Numbers` }
                    </Paragraph>
                    <Paragraph variant={ `help` }>
                      { `One number is included with your plan. ${phoneNumbersExtra ? `Extra numbers add ${phoneNumberPrice(phoneNumbersExtra)}.` : `Extra numbers are billed by country.`} Buy or release numbers on the Calls page.` }
                    </Paragraph>
                    <div className={ `mt-3 rounded-lg border border-gray-200` }>
                      { phoneNumbers.map((number, index) => (
                        <div
                          key={ number.id }
                          className={ `flex items-center justify-between px-5 py-4 ${index < phoneNumbers.length - 1 ? `border-b border-gray-100` : ``}` }
                        >
                          <div>
                            <div className={ `text-sm font-semibold text-paidniceGreen-800` }>
                              { `${number.phoneNumber} (${PHONE_NUMBER_COUNTRIES.find(c => c.code === number.isoCountry)?.name || number.isoCountry})` }
                            </div>
                            <div className={ `text-xs text-gray-500` }>{ number.organisationName }</div>
                          </div>
                          <div className={ `text-right text-sm text-gray-700` }>
                            <div>{ phoneNumberPrice(number.amountCents) }</div>
                            <When condition={ number.amountCents === 0 }>
                              <div className={ `text-xs text-gray-500` }>{ `Included` }</div>
                            </When>
                          </div>
                        </div>
                      )) }
                    </div>
                  </div>
                </When>

                <div>
                  <Paragraph variant={ `primary` }>
                    { `SMS Settings` }
                  </Paragraph>
                  <Paragraph variant={ `help` }>
                    { `Manage your monthly SMS limit and whether SMS billing is enabled for this subscription.` }
                  </Paragraph>
                  <div className={ `mt-3 rounded-lg border border-gray-200` }>
                    <div className={ `flex items-center justify-between border-b border-gray-100 px-5 py-4` }>
                      <div className={ `text-sm font-semibold text-paidniceGreen-800` }>
                        { `SMS Billing` }
                      </div>
                      <div className={ `flex items-center gap-4` }>
                        <Badge
                          color={ smsBillingBadgeColor }
                          message={ smsBillingBadgeLabel }
                          size={ `md` }
                        />
                        <button
                          className={ `cursor-pointer text-sm font-semibold ${smsBillingEnabled ? `text-paidniceRed-700 hover:text-paidniceRed-800` : `text-paidniceGreen-600 hover:text-paidniceGreen-700`}` }
                          onClick={ () => setShowSmsBillingModal(true) }
                        >
                          { smsBillingEnabled ? `Disable` : `Enable` }
                        </button>
                      </div>
                    </div>
                    <div className={ `flex items-center justify-between px-5 py-4` }>
                      <div className={ `flex items-center gap-3` }>
                        <span className={ `text-sm font-semibold text-paidniceGreen-800` }>
                          { `Monthly Limit` }
                        </span>
                        <SmsLimit subscription={ subscription } />
                      </div>
                      <button
                        className={ `cursor-pointer text-sm font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700` }
                        onClick={ onViewSmsInvoices }
                      >
                        { `View SMS invoices` }
                      </button>
                    </div>
                  </div>
                  <When condition={ showSmsBillingWarning }>
                    <Notification
                      type={ `warning` }
                      className={ `mt-4` }
                    >
                      { `SMS billing is currently disabled, so SMS reminders and responses are turned off.` }
                    </Notification>
                  </When>
                </div>

                <When condition={ Boolean(subscription.status) }>
                  <Wallet
                    subscriptionId={ subscription.id }
                    canPurchase={ subscription.status === SubscriptionStatus.ACTIVE }
                  />
                </When>
              </div>

              { /* Sidebar */ }
              <div className={ `flex flex-col gap-4` }>
                <When condition={ Boolean(subscription.status) }>
                  <div className={ `rounded-lg bg-paidniceGreen-800 p-6 text-white` }>
                    <div className={ `text-xs font-semibold uppercase tracking-wider text-paidniceGreen-300` }>
                      { `Current Plan` }
                    </div>
                    <When condition={ Boolean(planDisplayName) }>
                      <div className={ `mt-2 text-lg font-bold` }>
                        { planDisplayName }
                      </div>
                    </When>
                    <div className={ `mt-1 text-sm text-paidniceGreen-200` }>
                      { subscription.actualMonthlyPrice }
                    </div>
                    <div className={ `mt-4 flex justify-between border-t border-white/15 pt-4 text-sm text-paidniceGreen-200` }>
                      <span>{ `Status` }</span>
                      <span className={ `font-semibold ${statusTextClass}` }>{ subscription.status }</span>
                    </div>
                    <When condition={ Boolean(planDetails) }>
                      <div className={ `mt-2 flex justify-between text-sm text-paidniceGreen-200` }>
                        <span>{ `Renews` }</span>
                        <span className={ `text-white` }>{ planDetails?.periodEnd }</span>
                      </div>
                    </When>
                    <When condition={ isActiveSubscription }>
                      <button
                        className={ `mt-5 w-full cursor-pointer rounded-md bg-white py-2.5 text-sm font-bold text-paidniceGreen-800 transition-all hover:bg-paidniceGreen-50` }
                        onClick={ () => setShowChangePlanSection(true) }
                      >
                        { `Change Plan` }
                      </button>
                      <button
                        className={ `mt-2.5 w-full cursor-pointer rounded-md border border-white/25 py-2.5 text-sm font-semibold text-paidniceGreen-200 transition-all hover:bg-white/10 hover:text-white` }
                        onClick={ () => setShowCancelModal(true) }
                      >
                        { `Cancel Subscription` }
                      </button>
                    </When>
                  </div>
                </When>

                <div className={ `rounded-lg border border-gray-200 p-5` }>
                  <div className={ `text-sm font-bold text-paidniceGreen-800` }>
                    { `Billing` }
                  </div>
                  <Paragraph variant={ `help` }>
                    { `Add/update credit cards, change your billing address and view PDF invoices for your subscription.` }
                  </Paragraph>
                  <div className={ `mt-3 flex flex-col items-start gap-2.5` }>
                    <button
                      className={ `cursor-pointer text-sm font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700` }
                      onClick={ onManage }
                    >
                      { `Manage payment methods →` }
                    </button>
                    <button
                      className={ `cursor-pointer text-sm font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700` }
                      onClick={ onManage }
                    >
                      { `Manage billing addresses →` }
                    </button>
                    <button
                      className={ `cursor-pointer text-sm font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700` }
                      onClick={ onManage }
                    >
                      { `View subscription invoices →` }
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </When>
        </CardContent>
      </Card>

      <Modal
        title={ `Change Plan` }
        open={ showChangePlanSection }
        setOpen={ setShowChangePlanSection }
        width={ `stripe` }
      >
        <NewPlans inModal />
      </Modal>
      <CancelSubscriptionModal
        show={ showCancelModal }
        onConfirm={ onConfirmCancel }
        onCancel={ () => setShowCancelModal(false) }
        confirmLoading={ cancelLoading }
      />
      <SmsBillingModal
        show={ showSmsBillingModal }
        enabled={ smsBillingEnabled }
        onConfirm={ onToggleSmsBilling }
        onCancel={ () => setShowSmsBillingModal(false) }
        confirmLoading={ updateSubscriptionLoading }
      />
      <SmsInvoicesModal
        show={ showSmsInvoicesModal }
        invoices={ smsUsageInvoices }
        loading={ isSmsInvoicesLoading }
        onCancel={ () => setShowSmsInvoicesModal(false) }
      />
    </div>
  );
}
